import { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { apiGet } from "../api/client";
import { EmptyMessage } from "../components/EmptyMessage";
import type { FormSummary } from "./FormsPage";
import {
  FORM_PALETTE,
  documentForForm,
  type FormDocument,
  type FormElem,
} from "./formDocuments";

type OutletCtx = { form: FormSummary; doc: FormDocument };

/**
 * Form editor shell (`forms/form.html` / app.form.*).
 * Nested tabs: settings (main), edit, preview, responses.
 */
export function FormDetailPage() {
  const { id } = useParams();
  const [form, setForm] = useState<FormSummary | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    apiGet<FormSummary | FormSummary[]>("/forms")
      .then((d) => {
        if (cancelled) return;
        const list = Array.isArray(d) ? d : [d];
        setForm(
          list.find((f) => String(f.id) === String(id)) || list[0] || null,
        );
      })
      .catch(() => {
        if (!cancelled) setForm(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!form) {
    return (
      <EmptyMessage>
        <p>Loading form…</p>
      </EmptyMessage>
    );
  }

  const doc = documentForForm(id, form.name);
  const base = `/app/forms/${id}`;

  return (
    <div className="ne__forms">
      <header className="ne__page-header">
        <div className="ne__page-header__header">
          <h2 className="ne__page-header__title">
            Editing the form &quot;{form.name}&quot;
          </h2>
        </div>
        <ul className="ne__tab__group">
          {(
            [
              ["main", "ne__icon__settings--thin", "Settings"],
              ["edit", "ne__icon__pencil--thin", "Edit"],
              ["preview", "ne__icon__preview--thin", "Preview"],
              ["responses", "ne__icon__responses--thin", "Responses"],
            ] as const
          ).map(([seg, icon, label]) => (
            <li className="ne__tab" key={seg}>
              <NavLink
                className={({ isActive }) =>
                  `ne__tab__link${isActive ? " active" : ""}`
                }
                to={`${base}/${seg}`}
              >
                <i className={icon} />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        <ul className="ne__page-header__nav-secondary">
          <li>
            <button
              type="button"
              className="ne__btn ne__btn--secondary ne__btn--secondary--halo ne__btn--md"
              title="Save"
            >
              Save
            </button>
          </li>
        </ul>
      </header>
      <section className="ne__forms__tabs-view">
        <Outlet context={{ form, doc } satisfies OutletCtx} />
      </section>
    </div>
  );
}

function useFormOutlet() {
  return useOutletContext<OutletCtx>();
}

function FormField({ elem }: { elem: FormElem }) {
  const [yes, setYes] = useState<boolean | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  if (elem.type === "sect") {
    return (
      <header className="ne__forms__section">
        <h2>{elem.name}</h2>
        {elem.desc ? <p>{elem.desc}</p> : null}
      </header>
    );
  }

  if (elem.type === "bool") {
    return (
      <fieldset className="ne__forms__bool">
        <header>
          {elem.name ? <h4>{elem.name}</h4> : null}
          {elem.desc ? <p>{elem.desc}</p> : null}
        </header>
        <label>
          <ul className="ne__multi-select ne__multi-select--bool">
            <li className="ne__check-button">
              <label>
                <span
                  className={yes === true ? "checked" : undefined}
                  onClick={() => setYes(true)}
                >
                  Yes
                </span>
              </label>
            </li>
            <li className="ne__check-button">
              <label>
                <span
                  className={yes === false ? "checked" : undefined}
                  onClick={() => setYes(false)}
                >
                  No
                </span>
              </label>
            </li>
          </ul>
        </label>
      </fieldset>
    );
  }

  if (elem.type === "select") {
    return (
      <fieldset className="ne__forms__select">
        <header>
          {elem.name ? <h4>{elem.name}</h4> : null}
          {elem.desc ? <p>{elem.desc}</p> : null}
        </header>
        <label>
          <ul className="ne__multi-select">
            {(elem.options || []).map((opt) => (
              <li className="ne__check-button" key={opt.val}>
                <label>
                  <span
                    className={picked === opt.val ? "checked" : undefined}
                    onClick={() => setPicked(opt.val)}
                  >
                    {opt.dispval || opt.val}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </label>
      </fieldset>
    );
  }

  if (elem.type === "text") {
    return (
      <fieldset className="ne__forms__textarea">
        <label>
          <textarea
            className="ne__textarea ne__textarea--primary"
            rows={3}
            placeholder=" "
          />
          <span className="ne__placeholder">{elem.placeholder}</span>
        </label>
      </fieldset>
    );
  }

  if (elem.type === "num") {
    return (
      <fieldset className="ne__forms__number">
        <label>
          <input
            className="ftreen_input ne__input"
            type="number"
            placeholder=" "
          />
          <span className="ne__placeholder">{elem.placeholder}</span>
          {elem.unit ? (
            <span className="ne__forms__number__unit">{elem.unit}</span>
          ) : null}
        </label>
      </fieldset>
    );
  }

  if (elem.type === "signature") {
    return (
      <fieldset className="ne__forms__signature">
        <header>
          <h4>{elem.name}</h4>
          {elem.desc ? <p>{elem.desc}</p> : null}
        </header>
        <div className="ne__check-button">
          <label>
            <span
              className={agreed ? "checked" : undefined}
              onClick={() => setAgreed((v) => !v)}
            >
              Agree
            </span>
          </label>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset className="ne__forms__string">
      <label>
        <input className="ftreen_input ne__input" type="text" placeholder=" " />
        <span className="ne__placeholder">{elem.placeholder}</span>
      </label>
    </fieldset>
  );
}

function FormTree({ elems }: { elems: FormElem[] }) {
  return (
    <>
      {elems.map((elem) => (
        <div
          key={elem.id}
          className={`ne__forms__element ne__forms__element--12 ftreen_elem showelem showelem-${elem.type}`}
        >
          {elem.type === "sect" ? (
            <div className="ftreen_headings menuelem">
              <FormField elem={elem} />
            </div>
          ) : (
            <div className="ftreen_felem menuelem">
              <div className="ftreen_ictrl">
                <FormField elem={elem} />
              </div>
            </div>
          )}
          <div className="ftreen_children ne__forms__children menuchildren">
            {elem.children?.length ? <FormTree elems={elem.children} /> : null}
          </div>
        </div>
      ))}
    </>
  );
}

function OutlineTree({ elems }: { elems: FormElem[] }) {
  return (
    <ul className="menuchildren">
      {elems.map((elem) => (
        <li key={elem.id}>
          <p className="menuelem">
            <i className="fa fa-arrows" />{" "}
            {elem.children?.length ? (
              <i className="expand_icon fa fa-chevron-down" />
            ) : null}
            <i className={`elem_icon fa ${iconForType(elem.type)}`} />{" "}
            <span>{elem.name || elem.placeholder || elem.type}</span>
          </p>
          {elem.children?.length ? <OutlineTree elems={elem.children} /> : null}
        </li>
      ))}
    </ul>
  );
}

function iconForType(type: FormElem["type"]): string {
  switch (type) {
    case "sect":
      return "fa-header";
    case "date":
      return "fa-calendar";
    case "text":
      return "fa-align-left";
    case "bool":
      return "fa-check-square-o";
    case "select":
      return "fa-list-ul";
    case "num":
      return "fa-hashtag";
    case "signature":
      return "fa-pencil";
    default:
      return "fa-font";
  }
}

export function FormSettingsTab() {
  const { form, doc } = useFormOutlet();
  const [role, setRole] = useState(doc.role);

  return (
    <div className="metawrap">
      <fieldset className="ne__fieldset">
        <label className="ne__select__container">
          <input
            className="ne__input has-value"
            type="text"
            defaultValue={form.name}
            placeholder="Name of form"
          />
          <div className="ne__placeholder">Name</div>
        </label>
      </fieldset>
      <fieldset className="ne__fieldset">
        <label className="ne__select__container">
          <textarea
            className="ne__textarea ne__textarea--primary has-value"
            defaultValue={doc.desc}
            rows={3}
            placeholder="Description"
          />
          <div className="ne__placeholder">Description</div>
        </label>
      </fieldset>
      <fieldset className="ne__fieldset">
        <div className="ne__select__container">
          <header>
            <h4>Where should this form be used?</h4>
          </header>
          <ul className="ne__filter__list">
            <li className="ne-document-item ne__filter__item">
              {doc.parent}
              <i className="ne__icon ne__icon__tick--thicker" />
            </li>
          </ul>
        </div>
      </fieldset>
      <fieldset className="ne__fieldset">
        <div className="ne__select__container">
          <header>
            <h4>What is the purpose for this form?</h4>
          </header>
          <ul className="ne__filter__list">
            {["Intake", "Clinical", "Screening", "Consent", "Marketing"].map(
              (item) => (
                <li
                  key={item}
                  className="ne-document-item ne__filter__item"
                  onClick={() => setRole(item)}
                >
                  {item}
                  {role === item ? (
                    <i className="ne__icon ne__icon__tick--thicker" />
                  ) : null}
                </li>
              ),
            )}
          </ul>
        </div>
      </fieldset>
    </div>
  );
}

export function FormEditTab() {
  const { doc } = useFormOutlet();
  const [palette, setPalette] = useState<"common" | "custom">("custom");

  return (
    <div className="ne-form-edit">
      <div className="ne-form-edit-menu ne__forms__menu">
        <OutlineTree elems={doc.children} />
      </div>
      <div className="menuchildren ne-form-edit-main ne__forms__main ne__forms__main--edit">
        <FormTree elems={doc.children} />
      </div>
      <div className="ne-form-edit-tools ne__forms__tools">
        <ul className="innertabs">
          <li
            className={`innertab${palette === "common" ? " active" : ""}`}
            onClick={() => setPalette("common")}
          >
            Common fields
          </li>
          <li
            className={`innertab${palette === "custom" ? " active" : ""}`}
            onClick={() => setPalette("custom")}
          >
            Custom fields
          </li>
        </ul>
        <div className="etypewrap">
          {(palette === "common" ? FORM_PALETTE.common : FORM_PALETTE.custom).map(
            (field) => (
              <div className="etype" key={`${palette}-${field.title}`}>
                <i className={`etypeicon fa ${field.icon}`} />
                <p className="etypename">{field.title}</p>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

export function FormPreviewTab() {
  const { doc } = useFormOutlet();
  return (
    <div className="ne__forms__main ne__forms__main--preview" style={{ padding: "2.5vw 5%" }}>
      <FormTree elems={doc.children} />
    </div>
  );
}

export function FormResponsesTab() {
  const { form } = useFormOutlet();
  const rows = [
    {
      id: `${form.id}-r1`,
      created: "Aug 12, 2026, 9:14 AM",
      updated: "Aug 12, 2026, 9:18 AM",
      name: "Casey Morgan",
      email: "casey.morgan@example.test",
    },
    {
      id: `${form.id}-r2`,
      created: "Aug 10, 2026, 2:41 PM",
      updated: "Aug 10, 2026, 2:44 PM",
      name: "Riley Quinn",
      email: "riley.quinn@example.test",
    },
    {
      id: `${form.id}-r3`,
      created: "Aug 8, 2026, 11:03 AM",
      updated: "Aug 8, 2026, 11:09 AM",
      name: "Avery Brooks",
      email: "avery.brooks@example.test",
    },
  ].slice(0, Math.min(3, Math.max(1, Math.round((form.response_count || 3) / 20))));

  if (!form.response_count) {
    return (
      <div className="m1" style={{ padding: "2.5vw 5%" }}>
        <h4 className="ne__h4">Sorry, no responses to this form yet!</h4>
      </div>
    );
  }

  return (
    <div className="m1" style={{ padding: "2.5vw 5%" }}>
      <div className="formrespsw">
        <div className="panel">
          <table className="table ptnttbl">
            <thead>
              <tr>
                <th>Response ID</th>
                <th>First submitted</th>
                <th>Last updated</th>
                <th>Patient name</th>
                <th>Patient email</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr className="nicehover cp" key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.created}</td>
                  <td>{row.updated}</td>
                  <td>{row.name}</td>
                  <td>{row.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
